import { i18next, resolveTranslationLanguage, type MivoTranslationKey } from "@mivo/i18n";
import type {
    TherapistRegistrationNoticeState,
    TherapistRegistrationPlanCatalogEntry,
} from "./types";

export const DEFAULT_TRIAL_DAYS = 15;

export const PAID_PLAN_IDS = new Set<string>(["Pro"]);

export const REFERRAL_ELIGIBLE_PLAN_IDS = new Set<string>(["Basico", "Pro"]);

export const REFERRAL_SUBSCRIBER_DISCOUNT_PERCENT = 10;

export const REFERRAL_SUBSCRIBER_DISCOUNT_CYCLES = 3;

export type TherapistRegistrationNoticeReferralOptions = {
    active: true;
    planId: string;
};

function resolveLocale(locale?: string | null): MivoTranslationKey {
    return resolveTranslationLanguage(locale ?? i18next.language);
}

export function normalizeRegistrationPlanId(planId: string | null | undefined): string {
    if (!planId?.trim()) {
        return "Basico";
    }

    const trimmed = planId.trim();
    if (trimmed === "Free" || trimmed === "Basico" || trimmed === "Pro") {
        return trimmed;
    }

    const lower = trimmed.toLowerCase();
    if (lower === "gratuito" || lower === "freemium" || lower === "free") {
        return "Free";
    }

    if (lower === "básico" || lower === "basico" || lower === "basic") {
        return "Basico";
    }

    if (lower === "pro" || lower === "proplus" || lower === "pro+" || lower === "professional_30" || lower === "professional_50") {
        return "Pro";
    }

    return "Basico";
}

export function findRegistrationPlanInCatalog(
    catalog: readonly TherapistRegistrationPlanCatalogEntry[],
    planId: string | null | undefined,
): TherapistRegistrationPlanCatalogEntry | undefined {
    const slug = normalizeRegistrationPlanId(planId);
    return catalog.find((plan) => plan.id === slug);
}

export function formatRegistrationCatalogPlanPrice(plan: TherapistRegistrationPlanCatalogEntry): string | null {
    const priceLabel = plan.priceLabel?.trim() ?? "";
    const periodLabel = plan.periodLabel?.trim() ?? "";
    if (priceLabel && periodLabel) {
        return `${priceLabel}${periodLabel}`;
    }

    if (plan.priceBrl != null && Number.isFinite(plan.priceBrl)) {
        const amount = plan.priceBrl.toFixed(2).replace(".", ",");
        const period = periodLabel || "/mês";
        return `R$ ${amount}${period}`;
    }

    return null;
}

export function isPaidRegistrationPlan(planId: string | null | undefined): boolean {
    return PAID_PLAN_IDS.has(normalizeRegistrationPlanId(planId));
}

export function applyReferralSubscriberDiscount(catalogListPriceBrl: number): number {
    return (
        Math.round(
            (catalogListPriceBrl * (100 - REFERRAL_SUBSCRIBER_DISCOUNT_PERCENT)) / 100 * 100,
        ) / 100
    );
}

function resolvePlanPriceBrl(plan: TherapistRegistrationPlanCatalogEntry): number | null {
    if (plan.priceBrl != null && Number.isFinite(plan.priceBrl) && plan.priceBrl > 0) {
        return plan.priceBrl;
    }

    return null;
}

function formatBrlPerMonth(amount: number): string {
    return `R$ ${amount.toFixed(2).replace(".", ",")}/mês`;
}

function resolveReferralRegistrationNotice(
    planId: string,
    catalog: readonly TherapistRegistrationPlanCatalogEntry[],
    locale?: string | null,
    trialDaysFallback = DEFAULT_TRIAL_DAYS,
): TherapistRegistrationNoticeState {
    const resolvedLocale = resolveLocale(locale);
    const plan = findRegistrationPlanInCatalog(catalog, planId);
    if (!plan || !REFERRAL_ELIGIBLE_PLAN_IDS.has(plan.id)) {
        return { status: "unavailable" };
    }

    const priceBrl = resolvePlanPriceBrl(plan);
    if (priceBrl == null) {
        return { status: "unavailable" };
    }

    const fullPriceCopy = formatRegistrationCatalogPlanPrice(plan) ?? formatBrlPerMonth(priceBrl);
    const discountedCopy = formatBrlPerMonth(applyReferralSubscriberDiscount(priceBrl));
    const trialDays = plan.trialDays > 0 ? plan.trialDays : trialDaysFallback;

    return {
        status: "ready",
        text: resolvedLocale === "en"
            ? `You selected the ${plan.name} plan with a referral code (${discountedCopy} for the first ${REFERRAL_SUBSCRIBER_DISCOUNT_CYCLES} months, then ${fullPriceCopy}). Your professional registration starts in the "${trialDays}-day trial" period. After subscribing, you can cancel renewal at the next due date and keep access until the paid period ends.`
            : resolvedLocale === "es"
                ? `Has elegido el plan ${plan.name} con código de referencia (${discountedCopy} durante los primeros ${REFERRAL_SUBSCRIBER_DISCOUNT_CYCLES} meses y luego ${fullPriceCopy}). Tu registro profesional comienza en el período de prueba de ${trialDays} días. Tras suscribirte, puedes cancelar la renovación en el próximo vencimiento y seguir usando hasta el fin del período pagado.`
                : resolvedLocale === "de"
                    ? `Du hast den ${plan.name}-Plan mit Empfehlungscode gewählt (${discountedCopy} in den ersten ${REFERRAL_SUBSCRIBER_DISCOUNT_CYCLES} Monaten, danach ${fullPriceCopy}). Deine Registrierung startet in der ${trialDays}-Tage-Testphase. Nach dem Abonnement kannst du die Verlängerung zum nächsten Fälligkeitsdatum kündigen und bis zum Ende des bezahlten Zeitraums weiter nutzen.`
                    : `Você escolheu o plano ${plan.name} com código de indicação (${discountedCopy} nos ${REFERRAL_SUBSCRIBER_DISCOUNT_CYCLES} primeiros meses, depois ${fullPriceCopy}). Seu cadastro profissional inicia no período "Experimente por ${trialDays} dias". Após assinar, o cancelamento vale para o próximo vencimento e você continua usando até o fim do período pago.`,
    };
}

export function resolveTherapistRegistrationNotice(
    intendedPlanId: string | null | undefined,
    catalog: readonly TherapistRegistrationPlanCatalogEntry[] | undefined,
    catalogReady: boolean,
    trialDaysFallback = DEFAULT_TRIAL_DAYS,
    referral?: TherapistRegistrationNoticeReferralOptions,
    locale?: string | null,
): TherapistRegistrationNoticeState {
    const resolvedLocale = resolveLocale(locale);
    if (!catalogReady) {
        return { status: "loading" };
    }

    if (referral?.active) {
        return resolveReferralRegistrationNotice(
            referral.planId,
            catalog ?? [],
            resolvedLocale,
            trialDaysFallback,
        );
    }

    const plan = findRegistrationPlanInCatalog(catalog ?? [], intendedPlanId ?? "Basico");
    if (!plan) {
        return { status: "unavailable" };
    }

    const priceCopy = formatRegistrationCatalogPlanPrice(plan);
    if (!priceCopy && plan.id !== "Free") {
        return { status: "unavailable" };
    }

    const trialDays = plan.trialDays > 0 ? plan.trialDays : trialDaysFallback;

    if (plan.id === "Free") {
        return {
            status: "ready",
            text: resolvedLocale === "en"
                ? `You selected the ${plan.name} plan. After creating your account, you will have permanent free access to the features included in this plan.`
                : resolvedLocale === "es"
                    ? `Has elegido el plan ${plan.name}. Después de crear la cuenta, tendrás acceso gratuito permanente a las funcionalidades incluidas en este plan.`
                    : `Você escolheu o plano ${plan.name}. Após criar a conta, você terá acesso gratuito permanente com os recursos incluídos neste plano.`,
        };
    }

    return {
        status: "ready",
        text: resolvedLocale === "en"
            ? `You selected the ${plan.name} plan (${priceCopy} after ${trialDays} trial days). Your professional registration starts in the "Try it for ${trialDays} days" period. After that period, access may be blocked until the plan is purchased.`
            : resolvedLocale === "es"
                ? `Has elegido el plan ${plan.name} (${priceCopy} después de ${trialDays} días de prueba). Tu registro profesional comienza en el período "Pruébalo por ${trialDays} días". Después de ese plazo, el acceso podrá bloquearse hasta la contratación del plan.`
                : `Você escolheu o plano ${plan.name} (${priceCopy} após os ${trialDays} dias de experimentação). Seu cadastro profissional inicia no período "Experimente por ${trialDays} dias". Após esse prazo, o acesso poderá ser bloqueado até a contratação do plano.`,
    };
}

export function getTherapistRegistrationNotice(
    intendedPlanId?: string | null,
    catalog?: readonly TherapistRegistrationPlanCatalogEntry[],
    catalogReady = catalog != null,
    trialDaysFallback = DEFAULT_TRIAL_DAYS,
    locale?: string | null,
): string {
    const resolvedLocale = resolveLocale(locale);
    const notice = resolveTherapistRegistrationNotice(
        intendedPlanId,
        catalog,
        catalogReady,
        trialDaysFallback,
        undefined,
        resolvedLocale,
    );
    if (notice.status === "ready") {
        return notice.text;
    }
    if (notice.status === "loading") {
        return resolvedLocale === "en"
            ? "Loading plan information..."
            : resolvedLocale === "es"
                ? "Cargando información del plan..."
                : "Carregando informações do plano…";
    }
    return resolvedLocale === "en"
        ? "Plan unavailable at the moment."
        : resolvedLocale === "es"
            ? "Plan no disponible en este momento."
            : "Plano indisponível no momento.";
}
